<template>
  <div class="h-full">
    <div class="h-full flex flex-col">
      <div class="flex w-full pb-6 items-center flex-shrink-0 sm:pr-10 h-14">
        <div class="flex-grow flex items-center">
          <div class="text-13 font-600">{{ $t('projects.endpoints.title') }}</div>
        </div>

        <div class="ml-auto flex flex-row justify-center">
          <PillButton :text="$t('add_new')" primary @click="onCreateClicked" />
        </div>
      </div>

      <div class="w-full flex-grow overflow-auto pb-2 flex flex-col">
        <div class="mb-5 text-13">
          <span class="font-600 text-indigo-800">{{ $t('projects.endpoints.instance_url') }}</span>
          <div class="mt-2">{{ instanceUrl }}</div>
        </div>
        <div class="mb-5 mt-3 text-13">
          <div class="font-600 text-indigo-800 mb-3 text-13 flex items-center">
            <Icon name="settings_ethernet" class="w-5 h-5 mr-2 shrink-0" /> {{ $t('projects.endpoints.api_request_endpoints') }}
          </div>
          <template v-if="groupNames.length">
            <div v-for="(groupName, index) in groupNames" :key="index" class="pb-1">
              <div v-if="getEndpointsByGroupName(groupName).length" class="text-13 ml-4 text-semi-dark-text opacity-70 font-600 mt-3">{{ groupName }}</div>
              <TableElevated v-if="getEndpointsByGroupName(groupName).length" :items="getEndpointsByGroupName(groupName)" :headers="endpointHeaders" class="text-13 pr-3 sm:pr-10">
                <template v-slot:[`item.method`]="{ value }">
                  <Badge class="w-12" small v-if="value === 'GET'" info :text="value"></Badge>
                  <Badge class="w-12" small v-if="value === 'POST'" success :text="value"></Badge>
                  <Badge class="w-12" small v-if="value === 'DELETE'" error :text="value"></Badge>
                  <Badge class="w-12" small v-if="value === 'PUT'" warn :text="value"></Badge>
                </template>
                <template v-slot:[`item.actions`]="{ item: endpoint }">
                  <IconButton icon="link" class="sm:ml-1 text-primary" :title="$t('projects.endpoints.copy_url')" @click="onCopyClicked(endpoint)"></IconButton>
                  <IconButton icon="edit" class="sm:ml-1 text-primary" @click="onEditClicked(endpoint, 'api-request')"></IconButton>
                  <IconButton icon="delete" class="sm:ml-1 text-primary" @click="onDeleteClicked(endpoint, 'api-request')"></IconButton>
                </template>
              </TableElevated>
            </div>
          </template>
          <div v-else class="mt-2">
            <div class="text-center text-dark-text text-13">{{ $t('projects.endpoints.no_endpoints') }}</div>
          </div>
        </div>

        <div class="mb-5 mt-3 text-13">
          <div class="font-600 text-indigo-800 mb-3 text-13 flex items-center"><Icon name="schedule" class="w-5 h-5 mr-2 shrink-0" /> {{ $t('projects.scheduled_tasks.title') }}</div>
          <div class="mt-2">
            <TableElevated v-if="scheduledTasks.length" :items="scheduledTasks" :headers="scheduledTaskHeaders" class="text-13 pr-3 sm:pr-10">
              <template v-slot:[`item.period`]="{ item }">
                {{ getScheduledTaskText(item) }}
              </template>

              <template v-slot:[`item.status`]="{ value }">
                <Badge class="w-16" small v-if="value === 'Created'" info :text="value"></Badge>
                <Badge class="w-16" small v-if="value === 'Running'" success :text="value"></Badge>
                <Badge class="w-16" small v-if="value === 'Stopped'" error :text="value"></Badge>
              </template>
              <template v-slot:[`item.actions`]="{ item }">
                <IconButton icon="play_circle" v-if="item.status === 'Stopped'" class="sm:ml-1 text-primary" @click="onStartClicked(item)"></IconButton>
                <IconButton icon="stop_circle" v-if="item.status === 'Running'" class="sm:ml-1 text-primary" @click="onStopClicked(item)"></IconButton>
                <IconButton icon="edit" class="sm:ml-1 text-primary" @click="onEditClicked(item, 'scheduled-task')"></IconButton>
                <IconButton icon="delete" class="sm:ml-1 text-primary" @click="onDeleteClicked(item, 'scheduled-task')"></IconButton>
              </template>
            </TableElevated>
            <div v-else class="text-center text-dark-text text-13">{{ $t('projects.scheduled_tasks.no_scheduled_tasks') }}</div>
          </div>
        </div>
        <div class="mb-5 mt-3 text-13">
          <div class="font-600 text-indigo-800 mb-3 text-13 flex items-center"><Icon name="flex_direction" class="w-5 h-5 mr-2 shrink-0" /> {{ $t('projects.queue_consumers.title') }}</div>
          <div class="mt-2">
            <TableElevated v-if="queueConsumers.length" :items="queueConsumers" :headers="queueConsumerHeaders" class="text-13 pr-3 sm:pr-10">
              <template v-slot:[`item.queue`]="{ item }">
                {{ item.queue.label }}
              </template>
              <template v-slot:[`item.actions`]="{}">
                <IconButton icon="edit" class="sm:ml-1 text-primary"></IconButton>
                <IconButton icon="delete" class="sm:ml-1 text-primary"></IconButton>
              </template>
            </TableElevated>
            <div v-else class="text-center text-dark-text text-13">{{ $t('projects.queue_consumers.no_queue_consumers') }}</div>
          </div>
        </div>
      </div>
    </div>
    <Modal v-if="fetching">
      <div class="flex flex-col font-700 items-center justify-center px-20 my-10">
        <div>{{ $t('loading_data') }}</div>
        <Icon name="loading_dots" class="w-14 mt-3 text-primary" />
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex';
import DeleteModal from '../../modals/DeleteModal.vue';
import { WEEKDAYS } from '../../../constants';
import CreateProjectEndpointModal from '../../project/CreateProjectEndpointModal';

export default {
  name: 'Endpoints',
  data() {
    return {
      fetching: false,
      queueConsumerList: [],
      authProviders: [],
    };
  },
  computed: {
    ...mapState(['projectData']),
    ...mapGetters(['getProjectScheduledTasks', 'getProjectFlows', 'getProjectQueueConsumers']),
    projectId() {
      return this.$route.params.projectId;
    },
    endpoints() {
      if (!this.projectId) return [];
      const endpoints = this.projectData?.[this.projectId]?.endpoints || {};
      return Object.keys(endpoints).map((key) => {
        return {
          id: key,
          ...endpoints[key],
          flow: this.getProjectFlows()[endpoints[key].flow_id]?.name || '',
        };
      });
    },
    scheduledTasks() {
      return Object.values(this.getProjectScheduledTasks()).map((item) => {
        return {
          ...item,
          flow: this.getProjectFlows()[item.flow_id]?.name || '',
        };
      });
    },
    queueConsumers() {
      return Object.values(this.getProjectQueueConsumers());
    },
    groupNames() {
      const names = new Set(
        this.endpoints
          .map((endpoint) => endpoint.group_name)
          .filter((e) => e)
          .sort((a, b) => a.toLowerCase().localeCompare(b.toLowerCase())),
      );
      return [...names];
    },
    instanceUrl() {
      return `https://${this.projectId}.fidsy.cloud/api`;
    },
    endpointHeaders() {
      return [
        { key: 'method', title: this.$t('projects.endpoints.method'), width: '40px' },
        { key: 'path', title: this.$t('projects.endpoints.path') },
        { key: 'flow', title: this.$t('projects.flows.flow'), width: '300px' },
        { key: 'actions', title: this.$t('actions'), width: '50px' },
      ];
    },
    scheduledTaskHeaders() {
      return [
        { key: 'period', title: this.$t('projects.scheduled_tasks.schedule') },
        { key: 'flow', title: this.$t('projects.flows.flow'), width: '300px' },
        { key: 'status', title: this.$t('projects.scheduled_tasks.status'), width: '40px' },
        { key: 'actions', title: this.$t('actions'), width: '50px' },
      ];
    },
    queueConsumerHeaders() {
      return [
        { key: 'queue', title: 'Queue Name' },
        { key: 'batchSize', title: 'Batch Size' },
        { key: 'retries', title: 'Retries' },
        { key: 'actions', title: this.$t('actions'), width: '50px' },
      ];
    },
  },
  methods: {
    ...mapActions(['fetchProjectAgents', 'showToastMessage', 'deleteProjectEndpoint', 'deleteProjectScheduledTask', 'startProjectScheduledTask', 'stopProjectScheduledTask']),
    getScheduledTaskText(item) {
      let timeText;
      if (typeof item.hour === 'number' && item.hour < 10) {
        timeText = `0${item.hour}:`;
      } else if (typeof item.hour === 'string' && item.hour.length === 1) {
        timeText = `0${item.hour}:`;
      } else {
        timeText = `${item.hour}:`;
      }
      if (typeof item.minute === 'number' && item.minute < 10) {
        timeText += `0${item.minute} UTC`;
      } else if (typeof item.minute === 'string' && item.minute.length === 1) {
        timeText += `0${item.minute} UTC`;
      } else {
        timeText += `${item.minute} UTC`;
      }
      if (item.frequency === 'minute') {
        return `Every ${item.schedule} minute(s)`;
      }
      if (item.frequency === 'hour') {
        return `Every ${item.schedule} hour(s)`;
      }
      if (item.frequency === 'day') {
        return `Every ${item.schedule} day(s) at ${timeText}`;
      }
      if (item.frequency === 'month') {
        // Convert all numbers to their ordinal form
        let ordinalDay;
        const lastDigit = item.schedule % 10;
        switch (lastDigit) {
          case 1:
            ordinalDay = `${item.schedule}st`;
            break;
          case 2:
            ordinalDay = `${item.schedule}nd`;
            break;
          case 3:
            ordinalDay = `${item.schedule}rd`;
            break;
          default:
            ordinalDay = `${item.schedule}th`;
            break;
        }
        return `${ordinalDay} day of every month at ${timeText}`;
      }
      if (item.frequency === 'bi-month') {
        // Convert all numbers to their ordinal form
        let ordinalDay;
        const lastDigit = item.schedule % 10;
        switch (lastDigit) {
          case 1:
            ordinalDay = `${item.schedule}st`;
            break;
          case 2:
            ordinalDay = `${item.schedule}nd`;
            break;
          case 3:
            ordinalDay = `${item.schedule}rd`;
            break;
          default:
            ordinalDay = `${item.schedule}th`;
            break;
        }
        return `${ordinalDay} day of every other month at ${timeText}`;
      }
      if (item.frequency === 'quarter') {
        // Convert all numbers to their ordinal form
        let ordinalDay;
        const lastDigit = item.schedule % 10;
        switch (lastDigit) {
          case 1:
            ordinalDay = `${item.schedule}st`;
            break;
          case 2:
            ordinalDay = `${item.schedule}nd`;
            break;
          case 3:
            ordinalDay = `${item.schedule}rd`;
            break;
          default:
            ordinalDay = `${item.schedule}th`;
            break;
        }
        return `${ordinalDay} day of every quarter at ${timeText}`;
      }
      if (item.frequency === 'weekday') {
        const dayText = WEEKDAYS.find((w) => w.value === item.schedule).label;
        return `Every ${dayText} at ${timeText}`;
      }
      return '';
    },
    getEndpointsByGroupName(groupName) {
      return this.endpoints.filter((endpoint) => endpoint.group_name === groupName).sort((a, b) => a.path.toLowerCase().localeCompare(b.path.toLowerCase())) || [];
    },
    onCreateClicked() {
      this.$showModal(CreateProjectEndpointModal, { projectId: this.projectId, authProviders: this.authProviders });
    },
    async onCopyClicked(endpoint) {
      try {
        await navigator.clipboard.writeText(`${this.instanceUrl}${endpoint.path}`);
        this.showToastMessage({ message: this.$t('url_copied'), type: 'success' });
      } catch (err) {
        this.showToastMessage({ title: this.$t('url_copy_fail'), type: 'error' });
      }
    },
    onEditClicked(item, editDataType) {
      this.$showModal(CreateProjectEndpointModal, { projectId: this.projectId, editData: item, editDataType, authProviders: this.authProviders });
    },
    onDeleteClicked(item, editDataType) {
      if (editDataType === 'api-request') {
        this.$showModal(DeleteModal, {
          subtitle: this.$t('projects.endpoints.remove_endpoint_question'),
          onConfirm: () => this.onDeleteConfirmClicked(item, editDataType),
        });
      } else if (editDataType === 'scheduled-task') {
        this.$showModal(DeleteModal, {
          subtitle: this.$t('projects.scheduled_tasks.remove_endpoint_question'),
          onConfirm: () => this.onDeleteConfirmClicked(item, editDataType),
        });
      }
    },
    async onDeleteConfirmClicked(item, editDataType) {
      if (editDataType === 'api-request') {
        const response = await this.deleteProjectEndpoint({ project_id: this.projectId, endpoint_id: item.endpoint_id });
        if (response) {
          this.showToastMessage({ message: this.$t('projects.endpoints.removed_successfully'), type: 'success' });
        }
      } else if (editDataType === 'scheduled-task') {
        const response = await this.deleteProjectScheduledTask({ project_id: this.projectId, task_id: item.task_id });
        if (response) {
          this.showToastMessage({ message: this.$t('projects.scheduled_tasks.removed_successfully'), type: 'success' });
        }
      }
    },
    async onStartClicked(item) {
      const response = await this.startProjectScheduledTask({ project_id: this.projectId, task_id: item.task_id });
      if (response) {
        this.showToastMessage({ message: this.$t('projects.scheduled_tasks.started_successfully'), type: 'success' });
      }
    },
    async onStopClicked(item) {
      const response = await this.stopProjectScheduledTask({ project_id: this.projectId, task_id: item.task_id });
      if (response) {
        this.showToastMessage({ message: this.$t('projects.scheduled_tasks.stopped_successfully'), type: 'success' });
      }
    },
  },
  watch: {
    projectId: {
      async handler() {
        this.fetching = true;
        const response = await this.fetchProjectAgents({ projectId: this.projectId });
        if (response) {
          this.authProviders = response.authentication_providers;
        }
        this.fetching = false;
      },
      immediate: true,
    },
  },
};
</script>
